import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { listPages, listPictograms, countPictograms, getProfile, updateProfile } from '../api/endpoints';
import { useRole } from '../hooks/useRole';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { DEFAULT_VISIBLE_SOURCES, THIRD_PARTY_SOURCES, LEGACY_SOURCE_MAP } from '../config/pictogramSources';
import { Upload, Image, LayoutGrid, Archive, Users, Settings, Eye, EyeOff, FileText } from 'lucide-react';

export function DashboardPage() {
  useDocumentTitle('Dashboard');
  const { canEdit, isAdmin } = useRole();
  const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState(false);

  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
    staleTime: 5 * 60 * 1000,
  });

  const hiddenShortcuts: Set<string> = new Set(
    (profileQuery.data?.preferences?.hiddenShortcuts as string[] | undefined) ?? []
  );

  const savePrefsMutation = useMutation({
    mutationFn: (hidden: string[]) =>
      updateProfile({ preferences: { ...profileQuery.data?.preferences, hiddenShortcuts: hidden } }),
    onSuccess: (data) => {
      queryClient.setQueryData(['profile'], data);
    },
  });

  const toggleHide = useCallback((id: string) => {
    const next = new Set(hiddenShortcuts);
    if (next.has(id)) next.delete(id); else next.add(id);
    savePrefsMutation.mutate([...next]);
  }, [hiddenShortcuts, savePrefsMutation]);
  const pagesQuery = useQuery({
    queryKey: ['pages'],
    queryFn: () => listPages(),
  });

  // Compute user's visible sources (same logic as Library / Editor)
  const userVisibleSources = useMemo(() => {
    const profile = profileQuery.data;
    const base = new Set(profile?.visibleSources ?? DEFAULT_VISIBLE_SOURCES);
    const acks = profile?.licenseAcknowledgments ?? [];
    for (const src of THIRD_PARTY_SOURCES) {
      if (acks.some(a => a.source === src)) base.add(src);
    }
    for (const [legacy, mapped] of Object.entries(LEGACY_SOURCE_MAP)) {
      if (base.has(mapped)) base.add(legacy);
    }
    return base;
  }, [profileQuery.data]);

  // Canonical visible sources (without legacy aliases) for API filtering
  const visibleSourceParam = useMemo(() => {
    const profile = profileQuery.data;
    const base = new Set(profile?.visibleSources ?? DEFAULT_VISIBLE_SOURCES);
    const acks = profile?.licenseAcknowledgments ?? [];
    for (const src of THIRD_PARTY_SOURCES) {
      if (acks.some(a => a.source === src)) base.add(src);
    }
    return [...base].join(',');
  }, [profileQuery.data]);

  const countsQuery = useQuery({
    queryKey: ['pictogram-counts', visibleSourceParam],
    queryFn: () => countPictograms(visibleSourceParam ? { sources: visibleSourceParam } : undefined),
    staleTime: 15 * 60 * 1000, // 15 min — counts change rarely
    enabled: !!profileQuery.data,
  });

  // Fetch recent pictograms, filtered by visible sources server-side
  const recentQuery = useQuery({
    queryKey: ['pictograms-recent', visibleSourceParam],
    queryFn: () => listPictograms({ source: visibleSourceParam }),
    enabled: !!profileQuery.data,
    staleTime: 5 * 60 * 1000,
  });

  // Safety-net: also filter client-side in case of edge cases
  const visibleRecentItems = useMemo(
    () => recentQuery.data?.items.filter((p) => userVisibleSources.has(p.source)) ?? [],
    [recentQuery.data, userVisibleSources],
  );

  return (
    <div className="space-y-8">
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-gray-500 text-sm font-medium">
              <Image className="w-3.5 h-3.5" />
              {countsQuery.data?.total ?? '…'} Piktogramme
            </span>
            <span className="text-gray-300">·</span>
            <span className="inline-flex items-center gap-1.5 text-gray-500 text-sm font-medium">
              <FileText className="w-3.5 h-3.5" />
              {pagesQuery.data?.items.length ?? 0} Seiten
            </span>
          </div>
        </div>
        <p className="text-gray-500 mt-1">
          Willkommen bei Symbolbuch.de
          <span className="mx-2 text-gray-300">·</span>
          <Link to="/hallo" className="text-indigo-600 hover:text-indigo-700 font-medium">kurz vorgestellt</Link>
        </p>
      </div>

      {/* Quick actions */}
      {(() => {
        const shortcuts = [
          { id: 'upload', to: '/pictograms/upload', label: 'Piktogramm hochladen', desc: 'Neues Bild per Drag & Drop oder Einfügen hochladen', border: 'hover:border-indigo-300', icon: <Upload className="w-5 h-5 text-indigo-600" />, visible: canEdit },
          { id: 'library', to: '/pictograms', label: 'Bibliothek', desc: 'Piktogramme suchen und verwalten', border: 'hover:border-indigo-300', icon: <Image className="w-5 h-5 text-indigo-600" />, visible: true },
          { id: 'editor', to: '/editor', label: 'Neue Seite erstellen', desc: 'Kommunikationsseite mit Piktogrammen gestalten', border: 'hover:border-indigo-300', icon: <LayoutGrid className="w-5 h-5 text-indigo-600" />, visible: canEdit },
          { id: 'pages', to: '/pages', label: 'Seiten', desc: 'Gespeicherte Kommunikationsseiten anzeigen', border: 'hover:border-indigo-300', icon: <Archive className="w-5 h-5 text-indigo-600" />, visible: true },
          { id: 'admin', to: '/admin/users', label: 'Benutzerverwaltung', desc: 'Benutzer, Rollen und Registrierungen verwalten', border: 'hover:border-indigo-300', icon: <Users className="w-5 h-5 text-indigo-600" />, visible: isAdmin },
        ];

        const available = shortcuts.filter((s) => s.visible);
        const shown = available.filter((s) => editMode || !hiddenShortcuts.has(s.id));
        const hiddenCount = available.filter((s) => hiddenShortcuts.has(s.id)).length;

        return shown.length > 0 || hiddenCount > 0 ? (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-medium text-gray-400">Schnellzugriff</h2>
                {!editMode && hiddenCount > 0 && (
                  <span className="text-xs text-gray-300">{hiddenCount} ausgeblendet</span>
                )}
              </div>
              <button
                onClick={() => setEditMode(!editMode)}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${
                  editMode
                    ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
              >
              <Settings className="w-3.5 h-3.5" />
                {editMode ? 'Fertig' : 'Anpassen'}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {shown.map((s) => {
                const isHidden = hiddenShortcuts.has(s.id);
                return (
                  <div key={s.id} className="relative">
                    <Link
                      to={s.to}
                      className={`group flex flex-col bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md ${s.border} transition-all h-full ${
                        editMode && isHidden ? 'opacity-40' : ''
                      }`}
                    >
                      <div className="mb-3 shrink-0">
                        {s.icon}
                      </div>
                      <h2 className="font-semibold text-sm text-gray-900 mb-0.5">{s.label}</h2>
                      <p className="text-gray-500 text-xs leading-snug">{s.desc}</p>
                    </Link>
                    {editMode && (
                      <button
                        onClick={() => toggleHide(s.id)}
                        className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                          isHidden
                            ? 'bg-gray-200 text-gray-400 hover:bg-indigo-100 hover:text-indigo-600'
                            : 'bg-indigo-100 text-indigo-600 hover:bg-red-100 hover:text-red-500'
                        }`}
                        title={isHidden ? 'Einblenden' : 'Ausblenden'}
                        aria-label={isHidden ? 'Einblenden' : 'Ausblenden'}
                      >
                        {isHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null;
      })()}

      {/* Recent pages */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Zuletzt bearbeitet</h2>
          <Link to="/pages" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Alle anzeigen →</Link>
        </div>
        {pagesQuery.isLoading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : pagesQuery.data?.items.length ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100 overflow-hidden">
            {pagesQuery.data.items.slice(0, 3).map((page) => (
              <Link
                key={page.id}
                to={`/editor/${page.id}`}
                className="flex items-center justify-between p-4 hover:bg-indigo-50/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-gray-400" />
                  </div>
                  <span className="font-medium text-gray-900">{page.title}</span>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  page.status === 'published' ? 'bg-emerald-100 text-emerald-700'
                  : page.status === 'draft' ? 'bg-amber-100 text-amber-700'
                  : 'bg-gray-100 text-gray-500'
                }`}>{page.status === 'draft' ? 'Entwurf' : page.status === 'published' ? 'Veröffentlicht' : 'Archiviert'}</span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Noch keine Seiten vorhanden.</p>
            <Link to="/editor" className="text-indigo-600 text-sm font-medium hover:text-indigo-800 mt-1 inline-block">Erste Seite erstellen →</Link>
          </div>
        )}
      </section>

      {/* Recent pictograms */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Zuletzt hochgeladen</h2>
          <Link to="/pictograms" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Alle anzeigen →</Link>
        </div>
        {recentQuery.isLoading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : visibleRecentItems.length ? (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {visibleRecentItems.slice(0, 6).map((pic) => (
              <div key={pic.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 text-center hover:shadow-md transition-shadow">
                <img
                  src={`${import.meta.env.VITE_ASSETS_URL}/${pic.previewKey || pic.fileKey}`}
                  alt={pic.title}
                  className="w-16 h-16 mx-auto object-contain mb-2"
                />
                <span className="text-sm font-medium text-gray-700 block truncate">{pic.title}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Image className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Noch keine Piktogramme vorhanden.</p>
            <Link to="/pictograms/upload" className="text-indigo-600 text-sm font-medium hover:text-indigo-800 mt-1 inline-block">Erstes Piktogramm hochladen →</Link>
          </div>
        )}
      </section>
    </div>
  );
}
